import type { types } from '@bedc/types';

/**
 * Seeing the materials a layer — or a selection of layers — is built from.
 *
 * Layer provenance lives in `wohUtils.convertToBomObject`, whose
 * `drawingData[].input[].layersUsed` records the layer set each contribution was evaluated
 * over. Shared because the take-off tool's BoM preview and the exported PDF must group the
 * same way — two implementations would drift, and the point of the PDF section is that it
 * matches what the estimator ticked on screen.
 *
 * A canvas recipe references `layerIds` + `deductionLayerIds`, and that configured set —
 * not the individual layer — is the smallest attributable unit: a recipe's quantity (and
 * above all its deductions) cannot be split between the layers it spans. So a combination
 * here is the recipe's full layer set, drawing layers plus deductions. The evaluator does
 * not always say so per input — a formula over `measurement.height`/`measurement.pitch`
 * is evaluated one layer at a time, each contribution carrying only its own layer and
 * each deduction arriving alone — so inputs are grouped by the union of every layer their
 * recipe touched, folding both evaluator paths into the same combination with a net
 * quantity. Recipes configured over the same set share a group. Every contribution
 * belongs to exactly one combination, so combinations sum without double counting.
 *
 * A user-supplied quantity now lives on the recipe ingredient, so it belongs to exactly one
 * contribution and is attributable to the combination that produced it — `drawingData` carries
 * `isOverridden` per input.
 *
 * Pure functions so both callers are testable without a canvas.
 */

// ----------------------------------------------------------------------------
// TYPES
// ----------------------------------------------------------------------------

/** A BoM item as `wohUtils.convertToBomObject` returns it — before it is persisted. */
export type BomBreakdownItem = Omit<types.wholeHouse.bom.Item, 'id' | 'groupId'>;

type DrawingData = NonNullable<BomBreakdownItem['drawingData']>;

type DrawingInput = DrawingData[number]['input'][number];

/** One layer of a combination, and whether it subtracted rather than added. */
export type LayerRef = DrawingInput['layersUsed'][number];

/** A material's contribution within a single layer combination. */
export type LayerGroupRow = {
	/**
	 * Stable key within the group — see `makeRowKey`. Rows never merge across items, so a
	 * grouped row maps 1:1 to a flat-view row.
	 */
	key: string;

	material: BomBreakdownItem['material'];

	priceUnitRef: BomBreakdownItem['priceUnitRef'];

	/**
	 * The usages of the contributions in THIS combination — not the item-level `usages`,
	 * which `convertToBomObject` uniqs across every layer the material is drawn from and
	 * would attribute a usage to combinations that never produced it.
	 */
	usages: NonNullable<BomBreakdownItem['usages']>;

	/**
	 * Calculated quantity this combination contributes: wastage applied to additive
	 * inputs, deductions subtracted raw — the same sum `convertToBomObject` folds into
	 * `quantity.originalValue`, so combinations add back up to the flat view's total.
	 */
	quantity: number;

	/**
	 * The contributions behind `quantity`, narrowed to this combination and left in the
	 * shape `BoMPreviewQuantityBreakdown` reads. Narrower than the flat view's breakdown,
	 * which shows every input for the material regardless of which layers produced it.
	 */
	drawingData: DrawingData;
};

/** Every material produced by one distinct layer combination. */
export type LayerGroup = {
	/** Stable key built from the layer set. */
	key: string;

	/** Human-readable combination, in the `Added | -Deducted` form the breakdown uses. */
	label: string;

	layers: LayerRef[];

	rows: LayerGroupRow[];
};

// ----------------------------------------------------------------------------
// HELPERS
// ----------------------------------------------------------------------------

/** Label for contributions a recipe produced without being tied to any layer. */
export const noLayerGroupLabel = 'No layers';

/**
 * Numeric-aware so "Joist 50 ft" sorts before "Joist 100 ft" — plain lexicographic
 * ordering puts 100 first, which reads as unsorted on a list of sized materials.
 */
export const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });

/** Sign a layer carries within its combination. */
const layerSign = (layer: LayerRef) => (layer.isDeduction ? '-' : '+');

/** Key of a layer within a combination — the same layer may add in one and subtract in another. */
const layerKey = (layer: LayerRef) => `${layerSign(layer)}${layer.id}`;

/** Additive layers read first, then alphabetically; id only breaks a tie. */
const compareLayers = (a: LayerRef, b: LayerRef) =>
	Number(a.isDeduction) - Number(b.isDeduction) ||
	collator.compare(a.name, b.name) ||
	a.id.localeCompare(b.id);

/**
 * The layers of one input, deduplicated and ordered so two inputs over the same set land
 * in the same group regardless of the order the evaluator listed them in.
 */
const normaliseLayers = (layersUsed: LayerRef[] | undefined): LayerRef[] => {
	const byKey = new Map<string, LayerRef>();

	for (const layer of layersUsed ?? []) {
		byKey.set(layerKey(layer), layer);
	}

	return [...byKey.values()].sort(compareLayers);
};

const makeGroupKey = (layers: LayerRef[]) => layers.map(layerKey).join('|');

/**
 * Key of one material's row within a group.
 *
 * The item's position is what keeps rows from merging across items: two deduction-only
 * items of the same material both carry `usages: []`, and a key built from material+usages
 * would fold them into one row the flat view shows as two.
 *
 * Material and price unit ride along because the key is also what `aggregateLayerGroups`
 * — an exported function over money values — merges on. Handed groups from two separate
 * `groupBomItemsByLayer` passes, a bare position would sum two different materials under
 * whichever name it met first; with identity in the key that collision cannot form. It is
 * the React `<tr>` key too, so a reorder of the source items now remounts a row instead of
 * remapping it onto a different material.
 */
const makeRowKey = (itemIndex: number, item: BomBreakdownItem) =>
	`item-${itemIndex}|${item.material.id}|${item.priceUnitRef.id}`;

/** Additive layers read as their bare name; only a subtraction needs announcing. */
const makeGroupLabel = (layers: LayerRef[]) =>
	layers.length === 0
		? noLayerGroupLabel
		: layers.map((layer) => (layer.isDeduction ? `-${layer.name}` : layer.name)).join(' | ');

/** Signed contribution of one input, matching how `convertToBomObject` totals them. */
const signedQuantity = (input: DrawingInput) =>
	input.isDeduction ? -input.quantity.withWastage : input.quantity.withWastage;

/** A group whose every layer subtracts — a recipe configured with only deduction layers. */
const isDeductionOnly = (group: LayerGroup) =>
	group.layers.length > 0 && group.layers.every((layer) => layer.isDeduction);

/**
 * Whether a layer widens a group beyond what was ticked — an additive layer the selection
 * did not ask for, whose contribution cannot be split away from the ticked ones. It is
 * what makes a roll-up over such a group an upper bound rather than a total.
 *
 * A deduction layer is never "beyond" the selection even when it is not ticked: it only
 * ever reduces, and it is in the group precisely because the selection is entitled to it.
 * Counting one would put a "the total reaches past your selection" caveat on a number the
 * deduction pulled DOWN.
 *
 * `undefined` means nothing is ticked, so nothing is beyond anything.
 */
export const isLayerBeyondSelection = (
	layer: LayerRef,
	selectedLayerIds: Set<string> | undefined
) => !!selectedLayerIds && !layer.isDeduction && !selectedLayerIds.has(layer.id);

/**
 * Reading order: the plain single layers a user is most likely looking for, then the
 * combinations, with the layer-less and deduction-only groups — which only make sense
 * next to the contributions they reduce — trailing.
 */
const compareGroups = (a: LayerGroup, b: LayerGroup) =>
	Number(a.layers.length === 0) - Number(b.layers.length === 0) ||
	Number(isDeductionOnly(a)) - Number(isDeductionOnly(b)) ||
	a.layers.length - b.layers.length ||
	collator.compare(a.label, b.label) ||
	a.key.localeCompare(b.key);

/**
 * Materials read alphabetically; the key only breaks a tie the collator calls equal.
 *
 * A non-finite sum (a formula over a missing measurement yields NaN) is coerced to 0 on
 * the way out — `convertToBomObject` ends `computedQuantityRounded || 0`, so the flat
 * view and the persisted BoM both show 0 for the same contributions, and the grouped
 * view must not print `NaN` where they print 0.
 */
const finaliseRows = (rows: Iterable<LayerGroupRow>) =>
	[...rows]
		.map((row) => (Number.isFinite(row.quantity) ? row : { ...row, quantity: 0 }))
		.sort(
			(a, b) =>
				collator.compare(a.material.name, b.material.name) || a.key.localeCompare(b.key)
		);

/**
 * Appends a source breakdown into a row-owned one, keeping one envelope per canvas.
 *
 * `target` must be owned by the row (its envelopes are cloned on first insert below), so
 * appending in place is safe and folding k contributions stays O(k) — re-spreading the
 * accumulated arrays instead would make it O(k²), on every selection change.
 */
const appendDrawingData = (target: DrawingData, source: DrawingData): void => {
	for (const drawing of source) {
		const existing = target.find((candidate) => candidate.canvasId === drawing.canvasId);

		if (existing) {
			existing.input.push(...drawing.input);
		} else {
			target.push({ ...drawing, input: [...drawing.input] });
		}
	}
};

/** Adds a contribution into a row map, accumulating repeats of the same source item. */
const addRow = (
	rows: Map<string, LayerGroupRow>,
	key: string,
	row: Omit<LayerGroupRow, 'key' | 'drawingData' | 'usages'> & {
		drawingData: DrawingData;
		usage?: { id: types.wholeHouse.usage.Id; name: string };
	}
) => {
	const existing = rows.get(key);

	if (existing) {
		existing.quantity += row.quantity;
		appendDrawingData(existing.drawingData, row.drawingData);
		if (row.usage && !existing.usages.some((usage) => usage.id === row.usage?.id)) {
			existing.usages.push(row.usage);
		}
	} else {
		rows.set(key, {
			key,
			material: row.material,
			priceUnitRef: row.priceUnitRef,
			usages: row.usage ? [row.usage] : [],
			quantity: row.quantity,
			// Clone on the way in so the row owns its breakdown — `appendDrawingData`
			// writes in place, and must never write into the item it was read from.
			drawingData: row.drawingData.map((drawing) => ({
				...drawing,
				input: [...drawing.input],
			})),
		});
	}
};

// ----------------------------------------------------------------------------
// GROUPING
// ----------------------------------------------------------------------------

export type GroupBomItemsByLayerOptions = {
	/**
	 * Restrict to the drawing data of one canvas. The preview breakdown is built for a
	 * single canvas, but the item shape allows several.
	 */
	canvasId?: types.wholeHouse.canvas.Id;
};

/**
 * Groups BoM breakdown items by the layer combination that produced them.
 *
 * Materials fan out: one drawn from two different combinations appears once under each,
 * carrying only that combination's quantity.
 */
export const groupBomItemsByLayer = (
	items: BomBreakdownItem[],
	{ canvasId }: GroupBomItemsByLayerOptions = {}
): LayerGroup[] => {
	const filteredDrawings = (item: BomBreakdownItem) =>
		canvasId
			? (item.drawingData ?? []).filter((drawing) => drawing.canvasId === canvasId)
			: (item.drawingData ?? []);

	/**
	 * Recipe id → the union of every layer its contributions were evaluated over. The
	 * height/pitch evaluator path splits a recipe into per-layer inputs (deductions
	 * arriving alone), so an input's own `layersUsed` understates the combination; the
	 * union across the recipe's inputs recovers the configured set for both paths.
	 */
	const layersByRecipe = new Map<string, Map<string, LayerRef>>();

	for (const item of items) {
		for (const drawing of filteredDrawings(item)) {
			for (const input of drawing.input ?? []) {
				const union =
					layersByRecipe.get(input.canvasRecipeId) ?? new Map<string, LayerRef>();
				layersByRecipe.set(input.canvasRecipeId, union);

				for (const layer of input.layersUsed ?? []) {
					union.set(layerKey(layer), layer);
				}
			}
		}
	}

	/** Group key → group, with rows still keyed so repeats accumulate. */
	const groups = new Map<
		string,
		Omit<LayerGroup, 'rows'> & { rows: Map<string, LayerGroupRow> }
	>();

	items.forEach((item, itemIndex) => {
		for (const drawing of filteredDrawings(item)) {
			for (const input of drawing.input ?? []) {
				const layers = normaliseLayers([
					...(layersByRecipe.get(input.canvasRecipeId)?.values() ?? []),
				]);
				const key = makeGroupKey(layers);

				const group = groups.get(key) ?? {
					key,
					label: makeGroupLabel(layers),
					layers,
					rows: new Map<string, LayerGroupRow>(),
				};
				groups.set(key, group);

				addRow(group.rows, makeRowKey(itemIndex, item), {
					material: item.material,
					priceUnitRef: item.priceUnitRef,
					// Deductions carry a usage too, but the flat view's usage column only
					// reflects additive contributions — mirror that.
					usage: input.isDeduction
						? undefined
						: { id: input.usageId, name: input.usageName },
					quantity: signedQuantity(input),
					drawingData: [{ ...drawing, input: [input] }],
				});
			}
		}
	});

	return [...groups.values()]
		.map((group) => ({ ...group, rows: finaliseRows(group.rows.values()) }))
		.sort(compareGroups);
};

// ----------------------------------------------------------------------------
// FILTERING
// ----------------------------------------------------------------------------

/**
 * The combinations that involve any of the selected layers.
 *
 * A combination is kept when it touches the selection at all, so a recipe evaluated over
 * a selected layer *and* an unselected one still shows — its quantity is not divisible,
 * and hiding it would silently under-report the selected layer. The group's own layer
 * chips make the extra layers visible, and it is why the roll-up over the result is an
 * upper bound rather than a plain total for the ticked layers.
 *
 * Deductions never strand: a combination carries its recipe's whole layer set, so a
 * subtraction is inside the group it reduces and arrives (or stays away) with it.
 *
 * Layer-less groups are always kept: a recipe bound to no layers contributes regardless
 * of what is selected, and dropping it would silently omit its materials from the list.
 * (The roll-up excludes it — see `BoMPreviewLayerGroupTable` — since those materials are
 * not attributable to the selected layers.)
 *
 * An empty selection means "no filter": every combination is kept.
 */
export const filterLayerGroupsBySelection = (
	groups: LayerGroup[],
	selectedLayerIds: string[]
): LayerGroup[] => {
	if (selectedLayerIds.length === 0) {
		return groups;
	}

	const selected = new Set(selectedLayerIds);

	return groups.filter(
		(group) => group.layers.length === 0 || group.layers.some((layer) => selected.has(layer.id))
	);
};

// ----------------------------------------------------------------------------
// AGGREGATION
// ----------------------------------------------------------------------------

/**
 * Rolls a set of combinations into one material list — the materials needed to build all
 * of them together. Combinations partition the contributions, so this is a plain sum.
 */
export const aggregateLayerGroups = (
	groups: LayerGroup[],
	{ key, label }: { key: string; label: string }
): LayerGroup => {
	const rows = new Map<string, LayerGroupRow>();
	const layers = new Map<string, LayerRef>();

	for (const group of groups) {
		for (const layer of group.layers) {
			layers.set(layerKey(layer), layer);
		}

		for (const row of group.rows) {
			const existing = rows.get(row.key);

			if (existing) {
				existing.quantity += row.quantity;
				appendDrawingData(existing.drawingData, row.drawingData);
				for (const usage of row.usages) {
					if (!existing.usages.some((candidate) => candidate.id === usage.id)) {
						existing.usages.push(usage);
					}
				}
			} else {
				rows.set(row.key, {
					...row,
					usages: [...row.usages],
					drawingData: row.drawingData.map((drawing) => ({
						...drawing,
						input: [...drawing.input],
					})),
				});
			}
		}
	}

	return {
		key,
		label,
		layers: [...layers.values()],
		rows: finaliseRows(rows.values()),
	};
};
